import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import constructQueryParams from './construct-query-params'
import useForeignKeyListEndpoint from './useForeignKeyListEndpoint'

type UseGetDataFromForeignKeyAndDependenciesOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  foreignKey: Record<string, any>
  dependencyValues?: Record<string, string>
}
// https://react-hook-form.com/api/usewatch/
// This will return all values from the form, but in a stateful way.
// Whenever a form value changes, it will make PropertySelect rerender.
// This is needed so we can check for values from other fields this one depends on.
const useFormValues = () => {
  const { getValues } = useFormContext()
  return {
    ...useWatch(), // subscribe to form value updates
    ...getValues(), // always merge with latest form values
  }
}

export default function useGetDataFromForeignKeyAndDependencies({
  foreignKey,
  dependencyValues,
}: UseGetDataFromForeignKeyAndDependenciesOptions) {
  const { listEndpoint, listQueryParams = {} } = foreignKey

  const formValues = useFormValues()

  const { queryParams, hasAllDependencies } = constructQueryParams({
    queryParams: listQueryParams,
    otherDependencyValues: dependencyValues,
    values: formValues,
  })

  const query = useForeignKeyListEndpoint({
    url: listEndpoint,
    filters: {
      limit: '1000',
      ...queryParams,
    },
    enabled: Boolean(hasAllDependencies && listEndpoint),
  })

  const disabledIds = useMemo(() => {
    const tempDisabledIds: string[] = []
    if (foreignKey.listItemsDisabled) {
      // @ts-expect-error -- Ignore item not having a type
      foreignKey.listItemsDisabled.forEach((item) => {
        const value = dependencyValues?.[item.value]
        if (!value) return

        const dependentProperty = item.dependent_property
        if (
          dependentProperty &&
          item.valid_property_values.includes(formValues[dependentProperty])
        ) {
          tempDisabledIds.push(value)
        } else if (!dependentProperty) {
          tempDisabledIds.push(value)
        }
      })
    }
    return tempDisabledIds
  }, [dependencyValues, foreignKey.listItemsDisabled, formValues])

  return {
    ...query,
    disabledIds,
    hasAllDependencies,
  }
}
